import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const RestaurantContext = createContext(null);
export const useRestaurant = () => {
  const c = useContext(RestaurantContext);
  if (!c) throw new Error('useRestaurant must be within RestaurantProvider');
  return c;
};

export const RestaurantProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [activeRestaurant, setActiveRestaurant] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);

  // For non-owners, the active restaurant is fixed to the one they belong to.
  // For owners, it is whatever they pick (persisted in sessionStorage).
  const loadRestaurants = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const r = await api.get('/restaurants');
      setRestaurants(r.data.restaurants || []);
      return r.data.restaurants || [];
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setActiveRestaurant(null);
      setRestaurants([]);
      return;
    }
    (async () => {
      const list = await loadRestaurants();
      // Manager / employee: always their assigned restaurant
      if (user.role !== 'owner' && user.restaurant) {
        const own = list.find(r => r._id === (user.restaurant._id || user.restaurant));
        if (own) {
          setActiveRestaurant(own);
          // Set on api for future calls
          api.defaults.headers.common['X-Restaurant-Id'] = own._id;
        }
      } else if (user.role === 'owner') {
        // Restore previously-active restaurant if any
        const savedId = sessionStorage.getItem('activeRestaurantId');
        if (savedId) {
          const found = list.find(r => r._id === savedId);
          if (found) {
            setActiveRestaurant(found);
            api.defaults.headers.common['X-Restaurant-Id'] = found._id;
          }
        }
      }
    })();
  }, [isAuthenticated, user, loadRestaurants]);

  const selectRestaurant = (r) => {
    setActiveRestaurant(r);
    if (r) {
      sessionStorage.setItem('activeRestaurantId', r._id);
      api.defaults.headers.common['X-Restaurant-Id'] = r._id;
    } else {
      sessionStorage.removeItem('activeRestaurantId');
      delete api.defaults.headers.common['X-Restaurant-Id'];
    }
  };

  return (
    <RestaurantContext.Provider value={{
      activeRestaurant,
      restaurants,
      loading,
      selectRestaurant,
      refreshRestaurants: loadRestaurants
    }}>
      {children}
    </RestaurantContext.Provider>
  );
};
